import React, { useEffect, useMemo, useState } from "react";
import styles from "./EditProductPage.module.css";
import { X } from "lucide-react";
import { API_URL } from "../../config/api";

const EditProductPage = ({ open, product, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    ntin: "",
    price: "0.00",
    is_fast: false,
  });

  const [loadingFast, setLoadingFast] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ====== initial fill from cart ====== */
  useEffect(() => {
    if (!open) return;

    setForm({
      name: product?.name || "",
      ntin: product?.ntin || "",
      price: String(product?.price ?? "0.00"),
      is_fast: Boolean(product?.is_fast),
    });
  }, [open, product]);

  /* ====== (optional) fetch real is_fast from API (GET only) ======
     Если тебе вообще не нужен запрос — можешь удалить этот useEffect целиком.
  */
  useEffect(() => {
    if (!open || !product?.id) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    let cancelled = false;

    const fetchIsFast = async () => {
      setLoadingFast(true);
      try {
        const res = await fetch(`${API_URL}/api/products`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const text = await res.text();
        const json = JSON.parse(text);

        const list = Array.isArray(json?.data) ? json.data : [];
        const found = list.find((p) => Number(p.id) === Number(product.id));
        if (!found) return;

        if (!cancelled) {
          setForm((prev) => ({
            ...prev,
            is_fast: Boolean(found.is_fast),
          }));
        }
      } catch (e) {
        console.error("Ошибка GET /api/products:", e);
      } finally {
        if (!cancelled) setLoadingFast(false);
      }
    };

    fetchIsFast();
    return () => {
      cancelled = true;
    };
  }, [open, product?.id]);

  /* ====== ESC ====== */
  useEffect(() => {
    if (!open) return;

    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  const canSave = useMemo(() => {
    if (!open) return false;

    const nameOk = form.name.trim().length > 0;
    const ntinOk = /^[0-9]+$/.test(form.ntin.trim());
    const priceOk = !Number.isNaN(Number(form.price)) && Number(form.price) >= 0;

    return nameOk && ntinOk && priceOk;
  }, [open, form]);

  /* ====== LOCAL save (NO PUT) ====== */
  const handleSave = async () => {
    if (!product?.id) return;

    try {
      setSaving(true);

      onSave?.({
        ...product,
        name: form.name.trim(),
        ntin: form.ntin.trim(),
        price: Number(form.price) || 0,
        is_fast: Boolean(form.is_fast),
      });

      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.card} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Редактировать товар</h2>
          <button className={styles.iconBtn} onClick={onClose} type="button">
            <X size={20} />
          </button>
        </div>

        {!product ? (
          <div className={styles.emptyText}>Товар не выбран</div>
        ) : (
          <>
            <label className={styles.label}>
              <span>Наименование товара</span>
              <input
                className={styles.input}
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
              />
            </label>

            <label className={styles.label}>
              <span>Штрихкод</span>
              <input
                className={styles.input}
                value={form.ntin}
                onChange={(e) => {
                  if (/^[0-9]*$/.test(e.target.value)) {
                    setForm((p) => ({ ...p, ntin: e.target.value }));
                  }
                }}
              />
            </label>

            <label className={styles.label}>
              <span>Цена, ₸</span>
              <input
                className={styles.input}
                value={form.price}
                onChange={(e) =>
                  setForm((p) => ({ ...p, price: e.target.value }))
                }
              />
            </label>

            <label className={styles.fastRow}>
              <span className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={form.is_fast}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, is_fast: e.target.checked }))
                  }
                />
                <span className={styles.customCheckbox}></span>
              </span>

              <span className={styles.fastText}>
                Быстрый товар{" "}
                {loadingFast && (
                  <span style={{ opacity: 0.6 }}>(загрузка…)</span>
                )}
              </span>
            </label>

            <button
              className={styles.btnPrimary}
              onClick={handleSave}
              disabled={!canSave || saving}
              type="button"
            >
              {saving ? "Сохранение..." : "Сохранить"}
            </button>

            <button
              className={styles.btnSecondary}
              onClick={onClose}
              type="button"
              disabled={saving}
            >
              Отменить
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EditProductPage;
